import React, { useEffect, useState } from "react";
// @ts-ignore CSS is handled by the bundler; TypeScript has no declaration for it.
import "./CountdownPage.css";
import { Link } from "react-router-dom";

import {
  WAKE_UP_RELEASE_TIME,
} from "../config/wakeUpRelease";

const VIDEO_URL = "/files/wake-up";

const TARGET_DATE = WAKE_UP_RELEASE_TIME;

interface CountdownTime {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const ZERO_COUNTDOWN: CountdownTime = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

function calculateCountdown(): CountdownTime {
  const remainingMs = Math.max(0, TARGET_DATE - Date.now());
  const totalSeconds = Math.floor(remainingMs / 1000);

  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

const CountdownPage: React.FC = () => {
  const [countdown, setCountdown] =
    useState<CountdownTime>(calculateCountdown);

  const [isRevealed, setIsRevealed] = useState(
    () => Date.now() >= TARGET_DATE
  );

  const [glitchText, setGlitchText] = useState("WAKE UP");

  // ------------------------------------------------------------
  // Countdown timer
  // ------------------------------------------------------------

  useEffect(() => {
    document.title = "W4K3 UP";

    const updateCountdown = () => {
      const remaining = TARGET_DATE - Date.now();

      if (remaining <= 0) {
        setCountdown(ZERO_COUNTDOWN);
        setIsRevealed(true);
        return;
      }

      setCountdown(calculateCountdown());
    };

    // Immediately synchronize with the actual time.
    updateCountdown();

    // Recalculate from Date.now() on every tick to avoid drift.
    const intervalId = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  // ------------------------------------------------------------
  // Glitch effect
  // ------------------------------------------------------------

  useEffect(() => {
    if (isRevealed) {
      setGlitchText("WAKE UP");
      return;
    }

    const variants = ["WAK3 UP", "W4KE UP", "WAKE UP", "W4K3 UP"];

    let glitchTimeout: number;
    let restoreTimeout: number;

    const scheduleGlitch = () => {
      // Keep the effect occasional and unpredictable.
      const delay = 3000 + Math.random() * 5000;

      glitchTimeout = window.setTimeout(() => {
        const variant =
          variants[Math.floor(Math.random() * variants.length)];

        setGlitchText(variant);

        // Restore the original title after a brief flicker.
        restoreTimeout = window.setTimeout(() => {
          setGlitchText("WAKE UP");
          scheduleGlitch();
        }, 120 + Math.random() * 180);
      }, delay);
    };

    scheduleGlitch();

    return () => {
      window.clearTimeout(glitchTimeout);
      window.clearTimeout(restoreTimeout);
    };
  }, [isRevealed]);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <main
      className={`countdown-page ${
        isRevealed ? "countdown-page--revealed" : ""
      }`}
    >
      {/* Background atmosphere */}
      <div className="countdown-background" aria-hidden="true">
        <div className="countdown-glow countdown-glow--pink" />
        <div className="countdown-glow countdown-glow--lilac" />

        {/* Hypnotic spiral */}
        <svg
          className="countdown-spiral"
          viewBox="0 0 500 500"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="spiral-gradient"
              x1="50"
              y1="50"
              x2="450"
              y2="450"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#F5C6D9" />
              <stop offset="0.5" stopColor="#C9A4D9" />
              <stop offset="1" stopColor="#D9B18C" />
            </linearGradient>
          </defs>

          <path
            d="
              M250 250
              C250 250 250 250 250 250
              C270 250 285 235 285 215
              C285 185 260 165 230 170
              C190 175 165 210 175 250
              C185 300 235 330 285 310
              C350 285 370 215 335 160
              C290 90 195 85 135 140
              C60 210 80 325 165 380
              C270 450 405 390 430 280
              C455 160 375 55 265 40
            "
            stroke="url(#spiral-gradient)"
            strokeWidth="1.2"
            strokeLinecap="round"
          />

          <circle
            cx="250"
            cy="250"
            r="165"
            stroke="#E8B9D7"
            strokeWidth="0.5"
            strokeDasharray="2 12"
          />

          <circle
            cx="250"
            cy="250"
            r="205"
            stroke="#D9B18C"
            strokeWidth="0.5"
            strokeDasharray="1 18"
          />
        </svg>

        <div className="countdown-grain" />
      </div>

      {/* Main content */}
      <section className="countdown-content">
        {!isRevealed ? (<h1
            className="countdown-title"
            data-text={glitchText}
            aria-label="WAKE UP"
          >
            {glitchText}
          </h1>) : (<div></div>)
        }
        {!isRevealed ? (
          <>
            <div
              className="countdown-clock"
              role="timer"
              aria-label={`${countdown.days} days, ${countdown.hours} hours, ${countdown.minutes} minutes, ${countdown.seconds} seconds remaining`}
            >
              <div className="countdown-unit">
                <span className="countdown-value">
                  {pad(countdown.days)}
                </span>
                <span className="countdown-unit-label">DAYS</span>
              </div>

              <span className="countdown-separator" aria-hidden="true">
                :
              </span>

              <div className="countdown-unit">
                <span className="countdown-value">
                  {pad(countdown.hours)}
                </span>
                <span className="countdown-unit-label">HOURS</span>
              </div>

              <span className="countdown-separator" aria-hidden="true">
                :
              </span>

              <div className="countdown-unit">
                <span className="countdown-value">
                  {pad(countdown.minutes)}
                </span>
                <span className="countdown-unit-label">MINUTES</span>
              </div>

              <span className="countdown-separator" aria-hidden="true">
                :
              </span>

              <div className="countdown-unit countdown-unit--seconds">
                <span className="countdown-value countdown-seconds">
                  {pad(countdown.seconds)}
                </span>
                <span className="countdown-unit-label">SECONDS</span>
              </div>
            </div>
          </>
        ) : (
          <div className="countdown-reveal">
            <p className="countdown-reveal-message">SL33P</p>

            <Link to={VIDEO_URL} className="countdown-video-button">
              <span>YES</span>
            </Link>
          </div>
        )}
      </section>

    </main>
  );
};

export default CountdownPage;